/**
 * Fit the active project's canvas to the screen it is actually played on, by
 * MEASUREMENT rather than by preset.
 *
 * WHY MEASUREMENT AND NOT A PRESET. The operator gigs on an iPad 8th gen and
 * reported dead bands around the app across six rounds. Each round narrowed it
 * and each round was still wrong, because the target kept moving:
 *   - the only "iPad" preset was an Air / Pro 11-inch, a different ASPECT
 *   - adding a 10.2-inch preset (1080x810) got much closer, and still left a
 *     visible gap on every edge
 *   - MEASURED off their screenshot: the shell rendered 1068 points wide in a
 *     1080-point screen, at scale 0.989
 * What a device hands a standalone PWA is not reliably its spec-sheet size. A
 * preset can therefore never be right by construction, and `fitScale`'s
 * `min(1, ...)` means a shell that comes up short can never grow back. Nothing
 * inside the shell can reach an edge the shell does not reach.
 *
 * So: take the real measured box and make the canvas exactly that. Scale
 * becomes 1.0 and the gap is arithmetically impossible.
 *
 * ⚠️ "ARITHMETICALLY IMPOSSIBLE" IS ONLY AS GOOD AS THE MEASUREMENT, and that
 * sentence talked four later rounds out of checking it. It is impossible for
 * the shell to gap INSIDE the window; it says nothing about the window being
 * smaller than the screen, which is WebKit #254868 and was the actual fault.
 * The box passed in here must come from `trueViewport.ts`, never from a raw
 * viewport API - all of them return the same corrupted height.
 *
 * FOUR GUARDS, because silently rewriting someone's gig layout is its own
 * failure mode:
 *
 *   1. INSTALLED APP ONLY. `canvas` is part of the project and therefore
 *      SYNCS. A desktop browser tab auto-fitting to 1280x900 would push that
 *      frame to the iPad and wreck the rig. The frame must only ever be set by
 *      the device the project is played on, which is the installed one. Both
 *      `display-mode: standalone` and `navigator.standalone` are checked -
 *      the second is the belt-and-braces one on iOS.
 *      AND ON THE PROJECT'S KIND OF DEVICE (2026-10-05, operator-approved).
 *      "Installed" was taken to mean "the iPad", but Chrome installs PWAs on a
 *      desktop too, and an installed desktop window opening an iPad project
 *      would fit the iPad's frame to that window, the exact wreck guard 1
 *      exists to stop. Found while adding `maybeRecordReserve`, whose own
 *      harness run recorded a desktop's numbers into an iPad project.
 *   2. NOTHING MAY BE CLIPPED. Every item on every page must fit the new
 *      canvas, tolerance zero. "Mostly inside" still means a control the
 *      operator cannot reach mid-set.
 *   3. Only when it actually changes something, beyond a pixel of rounding.
 *   4. ONCE PER PROJECT, recorded per-device outside the project document. A
 *      deliberate switch back must not be undone on next launch - that is a
 *      fight the operator cannot see and cannot win.
 */

import { canvasSizeOf, STATUS_BAR_FLOOR } from './devices.ts';
import { isStandalone, safeAreaInsets } from './trueViewport.ts';
import { activeProject, setProjectCanvas, setProjectReserve } from './projectStore.ts';

/* v4, bumped 2026-09-22 (second bump the same day - see below).
   A fit is recorded per project so a deliberate switch back is not undone on
   next launch. That also means a project pinned against a WRONG measurement
   stays pinned forever, so the key is bumped whenever the measurement itself
   is corrected - which re-arms auto-fit exactly once, per project, against the
   new number.

   v2 -> v3 was bumped for `window.innerHeight` under-reporting by 32 points.
   That diagnosis was wrong in a way the bump could not fix: `clientHeight`
   under-reports by the SAME 32 points, because WebKit #254868 corrupts every
   height API together on an installed iOS web app. So v3 re-fitted the
   operator's project from 778 to... 778, and the strip did not move. The
   stored doc still read `canvas: {w:1080, h:778}` eight hours after that
   shipped, which is what finally identified the real fault.

   v4 re-armed against `100vh` (810). It fired, the project adopted 1080x810,
   and the strip STILL did not move - because 810 is the screen and only 778 of
   it can be painted. v4's frame is therefore 32pt of hidden canvas on any
   project it touched.

   v5 went back to the short height on the reading that 810 was unpaintable.
   That reading was wrong: the clip was `html, body { height: 100% }` plus our
   own `overflow: hidden`, not the compositor, so v4's 810 had been right and
   was cut off by its own container.

   v6 re-arms against the true screen height, now that the body is allowed to
   be that tall. The frame still only changes if it actually differs, and the
   no-clipping and installed-app guards are unaffected. */
const KEY = 'opbuttons.autofit.v6';

function fittedIds(): string[] {
  try {
    const raw = localStorage.getItem(KEY);
    const v: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function markFitted(id: string): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(Array.from(new Set([...fittedIds(), id])).slice(-50)));
  } catch { /* private mode / quota */ }
}

/** Forget that a project was fitted, so it can be offered again. */
export function clearAutoFitMemory(): void {
  try { localStorage.removeItem(KEY); } catch { /* */ }
}

/** How much of a frame is NOT canvas: the chrome bar plus the device's
 *  status-bar / home-indicator strips. Controls are positioned inside
 *  `.scaled-canvas-inner`, below all of that, so "fits the frame" has to mean
 *  "fits the frame minus this". It is the same for any frame on one device, so
 *  the live DOM answers it for a frame that is not applied yet. Both reads are
 *  layout px (offsetHeight / clientHeight ignore the shell's scale transform).
 *
 *  Added 2026-10-05: the overhang count compared item bottoms against the
 *  WHOLE frame height, 62pt more than a control can use on the operator's iPad
 *  (38 bar + 24 status bar), so a fit could clip a bottom-row control while
 *  reporting nothing clipped. Zero when there is no shell to measure. */
function chromeInsets(): { w: number; h: number } {
  if (typeof document === 'undefined') return { w: 0, h: 0 };
  const shell = document.querySelector('.shell') as HTMLElement | null;
  const inner = document.querySelector('.scaled-canvas-inner') as HTMLElement | null;
  if (!shell || !inner) return { w: 0, h: 0 };
  return {
    w: Math.max(0, shell.offsetWidth - inner.clientWidth),
    h: Math.max(0, shell.offsetHeight - inner.clientHeight),
  };
}

export interface AutoFitResult {
  from: { w: number; h: number };
  to: { w: number; h: number };
  /** Controls sitting outside the new frame. Zero means the fit is free. */
  overhang: number;
}

/** What an exact fit to this box would change, or null when it changes nothing.
 *  Read by `applyExactFit` (and so by the automatic fit). The Navigate banner
 *  that offered it manually was removed 2026-09-30 by the operator, once
 *  Navigate worked on phones: it offered to redraw a synced gig project for
 *  whatever screen opened the menu. */
export function exactFitAvailable(vw: number, vh: number): AutoFitResult | null {
  const p = activeProject();
  if (!p) return null;
  const w = Math.round(vw);
  const h = Math.round(vh);
  if (!(w > 0 && h > 0)) return null;

  const from = canvasSizeOf(p);
  if (Math.abs(from.w - w) <= 1 && Math.abs(from.h - h) <= 1) return null;

  /* COUNT the overhang, do not refuse on it.
     This used to `return null` the moment one control sat outside the new
     frame, which made the banner vanish too - so on the operator's rig the
     app reported 96% and offered nothing, with no way to find out why. A
     guard that declines silently is indistinguishable from a feature that
     does not work. The count is surfaced instead, and the automatic path
     still refuses to move anything on its own. */
  const chrome = chromeInsets();
  const usableW = w - chrome.w;
  const usableH = h - chrome.h;
  let overhang = 0;
  for (const page of p.pages ?? []) {
    for (const it of page.items ?? []) {
      const i = it as unknown as { x?: number; y?: number; w?: number; h?: number };
      if ((i.x ?? 0) + (i.w ?? 0) > usableW || (i.y ?? 0) + (i.h ?? 0) > usableH) overhang++;
    }
  }
  return { from, to: { w, h }, overhang };
}

/**
 * Apply an exact fit, refusing whenever a control would fall outside the new
 * frame. The frame is this app's geometry and ours to correct, but WHERE A
 * CONTROL SITS is the operator's muscle memory on a stage, and moving one
 * uninvited is a worse failure than a 4% gap (DEC-210).
 *
 * A `nudge` option that clamped overhanging controls inside used to live here
 * for the Navigate "Fit to this screen" banner; the banner went 2026-09-30 and
 * the option, with `nudgeItemsInside`, was removed 2026-10-18 with no caller
 * left (git `58776da` has both, plus the one-undo batch they needed).
 */
export function applyExactFit(vw: number, vh: number): AutoFitResult | null {
  const p = activeProject();
  const r = exactFitAvailable(vw, vh);
  if (!p || !r || r.overhang > 0) return null;
  setProjectCanvas(p.id, r.to.w, r.to.h);
  markFitted(p.id);
  return r;
}

/**
 * Fit automatically, if all four guards pass. Returns what changed, or null.
 *
 * `vw`/`vh` MUST be the measured fit box the shell is scaled into, not
 * `window.inner*` - those differ, and the difference is the entire bug.
 */
export function maybeAutoFit(vw: number, vh: number): AutoFitResult | null {
  if (!isStandalone()) return null;
  const p = activeProject();
  if (!p) return null;
  if (!onProjectsKindOfDevice(p.device)) return null;
  if (fittedIds().includes(p.id)) return null;
  return applyExactFit(vw, vh);
}

/**
 * Record the strips THIS installed device gives up to iOS into the project
 * (`Project.reserve`), so every other screen previews the same canvas.
 * Returns what it recorded, or null when it changed nothing.
 *
 * The desktop cannot know these: they exist only in an installed app, and the
 * device table's guess went stale (24pt top in September; iPadOS 26.6.1
 * reports 32, measured 2026-10-05). So the device that plays the project
 * measures them, exactly as it measures the frame (DEC-210).
 *
 * Guards, each for a reason:
 *   - INSTALLED APP ONLY, like the auto-fit: a browser tab has no status-bar
 *     strip, and the value syncs.
 *   - THE PROJECT'S KIND OF DEVICE only: an iPad records into an iPad
 *     project, an iPhone into an iPhone one. An installed desktop app (Chrome
 *     installs PWAs too) reports no inset and would record 24 into an iPad
 *     project - measured in the harness, the exact bug this exists to stop.
 *   - THE PROJECT'S OWN ORIENTATION only: portrait and landscape insets differ
 *     on a phone, and a rotated device would record the wrong pair.
 *   - NEVER SHRINKS, only grows edge by edge. If two devices play one project
 *     they would otherwise overwrite each other on every launch; the larger
 *     strip is the one that fits both. A strip larger than needed costs a few
 *     points of canvas; one smaller than needed cuts a control off on stage.
 *   - Written only when it changes, so a launch normally writes nothing.
 */
export function maybeRecordReserve(): { top: number; bottom: number } | null {
  if (!isStandalone()) return null;
  const p = activeProject();
  if (!p) return null;
  if (!onProjectsKindOfDevice(p.device)) return null;
  const landscape = window.innerWidth >= window.innerHeight;
  if ((p.orientation === 'landscape') !== landscape) return null;
  const env = safeAreaInsets();
  const measured = { top: Math.max(env.top, STATUS_BAR_FLOOR), bottom: env.bottom };
  const prev = p.reserve;
  const next = prev
    ? { top: Math.max(prev.top, measured.top), bottom: Math.max(prev.bottom, measured.bottom) }
    : measured;
  if (prev && prev.top === next.top && prev.bottom === next.bottom) return null;
  setProjectReserve(p.id, next.top, next.bottom);
  return next;
}

/** Is this screen the kind of device the project is drawn for? Every preset is
 *  an iPad or an iPhone; a desktop is never one. Shared by both automatic
 *  writers of synced frame data, so they cannot disagree about it. */
function onProjectsKindOfDevice(device: string): boolean {
  return deviceClass() === (device.startsWith('iphone') ? 'iphone' : 'ipad');
}

/** iPad / iPhone / anything else. iPadOS reports a Mac user agent; a touch
 *  screen is what tells them apart. The harness can claim one with
 *  `opbuttons.frame.assumeDevice` (never written by the app), because
 *  Playwright cannot be an iPad and an untestable guard proves nothing. */
function deviceClass(): 'ipad' | 'iphone' | 'other' {
  try {
    const forced = localStorage.getItem('opbuttons.frame.assumeDevice');
    if (forced === 'ipad' || forced === 'iphone') return forced;
  } catch { /* private mode */ }
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return 'iphone';
  if (/iPad/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return 'ipad';
  return 'other';
}
